<template>
  <form :class="$style.form" @submit.prevent="onSubmit">
    <UiKitTypography :type="MAP.H04D" :class="$style.title">
      Welcome to the HackenProof Dashboard
    </UiKitTypography>
    <div :class="$style.logInWith">
      <a
        :class="$style.logInWithButton"
        href="/api/internal/users/auth/google_oauth2"
        rel="nofollow"
        data-method="post"
      >
        <UiKitButton :variant="'outlined'">
          <template #left-icon>
            <img src="@/images/icons/GoogleLogo.svg" alt="Google" />
          </template>
          Log in with Google
        </UiKitButton>
      </a>
      <a
        :class="$style.logInWithButton"
        href="/api/internal/users/auth/github"
        rel="nofollow"
        data-method="post"
      >
        <UiKitButton :variant="'outlined'">
          <template #left-icon>
            <img src="@/images/icons/GithubLogo.svg" alt="Github" />
          </template>
          Log in with Github
        </UiKitButton>
      </a>
    </div>
    <UiKitDivider :class="$style.divider" text="Or" />
    <div :class="$style.inputs">
      <UiKitInput
        v-model="email"
        label="Email"
        placeholder="Enter your email"
        v-bind="emailAttrs"
        type="text"
        :autocomplete="false"
        :error="errors.email"
      />
      <UiKitInput
        v-model="password"
        label="Password"
        placeholder="Enter your password"
        v-bind="passwordAttrs"
        type="password"
        :autocomplete="false"
        :error="errors.password"
      />
    </div>
    <div :class="$style.checkbox">
      <UiKitCheckbox
        v-model="rememberMe"
        :name="'rememberMe'"
        :id="'rememberMe'"
      >
        <UiKitTypography :type="MAP.B02D"
          >Remember me</UiKitTypography
        >
      </UiKitCheckbox>
      <UiKitTypography :type="MAP.B01D" :class="$style.forgotPassword">
        <a :href="`/password_resets`"> Forgot password? </a>
      </UiKitTypography>
    </div>
    <UiKitButton
      :big="true"
      :fullwidth="true"
      :class="$style.button"
      :variant="'black'"
      type="submit"
      :disabled="disabled"
    >
      Login
    </UiKitButton>
    <UiKitTypography :type="MAP.B02D" :class="$style.link">
      New in HackenProof? Start now
      <UiKitTypography
        :type="MAP.B01D"
        :class="$style.signUpLink"
        @click="isSigningUp = true"
      >
        Sign up
      </UiKitTypography>
    </UiKitTypography>
  </form>
</template>

<script setup>
import { useForm } from "@/validation";
import * as yup from "yup";
import UiKitInput from "@/ui-kit/Input.vue";
import UiKitTypography from "@/ui-kit/Typography.vue";
import { MAP } from "@/constants/typography";
import UiKitCheckbox from "@/ui-kit/Checkbox.vue";
import UiKitButton from "@/ui-kit/Button.vue";
import UiKitDivider from "@/ui-kit/Divider.vue";
import { inject, watchEffect, ref, nextTick, onMounted } from "vue";
import { INJECT_KEYS } from "@/constants/injectKeys";
import { api } from "@/api/httpClient";
import { PASSWORD, REGEX } from "@/constants/validation";
import { handleResponseErrors } from "@/utils";
import { useReCaptcha } from "vue-recaptcha-v3";

const { executeRecaptcha } = useReCaptcha();

const disabled = ref(false);
const isSigningUp = inject(INJECT_KEYS.SIGN_UP_OPEN_DRAWER);

// Setup form with validation
const { values, defineField, handleSubmit, errors, setErrors, meta, validate } =
  useForm({
    validationSchema: yup.object({
      email: yup.string().required().matches(REGEX.EMAIL, "Invalid email"),
      password: yup
        .string()
        .min(PASSWORD.MIN_LENGTH)
        .max(PASSWORD.MAX_LENGTH)
        .required(),
    }),
    initialValues: {
      rememberMe: false,
    },
  });

const [email, emailAttrs] = defineField("email");
const [password, passwordAttrs] = defineField("password");
const [rememberMe, rememberMeAttrs] = defineField("rememberMe");

// Clear any lingering errors on component mount
onMounted(() => {
  setErrors({});
});

const handleSignIn = async () => {
  disabled.value = true;

  try {
    // Get reCAPTCHA token
    const token = await executeRecaptcha("sign_in");

    await api.post("/internal/users/sign_in", {
      user: {
        email: values.email,
        password: values.password,
        remember_me: values.rememberMe,
      },
      recaptcha_token: token,
    });
  } catch (error) {
    disabled.value = false;
    handleResponseErrors(error.data, setErrors);
  }
};

const handleSignInWithGoogle = () => {
  const form = document.createElement("form");
  form.method = "post";
  form.action = "/internal/users/auth/google_oauth2";
  document.body.appendChild(form);
  form.submit();
};

const onSubmit = handleSubmit(async (values) => {
  // Manually validate the form - this is the only place validation will happen
  const { valid } = await validate();
  if (!valid) {
    return;
  }

  handleSignIn();
});

watchEffect(() => {
  if (disabled.value) {
    console.log("disabled");
  }
});
</script>

<style module lang="scss">
.form {
  display: flex;
  flex-direction: column;
  width: 612px;

  @media screen and (max-width: 1200px) {
    width: 480px;
  }

  @media screen and (max-width: 990px) {
    width: 100%;
    padding: 0 40px;
  }

  @media screen and (max-width: 760px) {
    padding: 0 16px;
  }

  .title {
    text-align: center;
    margin-bottom: 40px;

    @media screen and (max-width: 1430px) {
      width: 400px;
      align-self: center;
    }

    @media screen and (max-width: 760px) {
      width: 100%;
      max-width: 400px;
    }
  }

  .inputs {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 40px;
  }

  .divider {
    margin-bottom: 40px;
  }

  .checkbox {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;

    @media screen and (max-width: 400px) {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
    }

    .forgotPassword {
      border-bottom: 1px solid #050505;
      cursor: pointer;

      a {
        color: #050505;
      }

      @media screen and (max-width: 400px) {
        align-self: flex-end;
      }
    }
  }

  .logInWith {
    display: flex;
    gap: 8px;
    margin-bottom: 40px;

    @media screen and (max-width: 760px) {
      flex-direction: column;
      align-items: stretch;
    }

    .logInWithButton {
      flex: 1;
      height: 44px;
      min-height: 44px;
      border-radius: 8px;

      button {
        height: 44px;
        min-height: 44px;
        border-radius: 8px;
        width: 100%;
      }

      @media screen and (max-width: 760px) {
        flex-shrink: 0;
        width: 100%;
      }

      outline: 1px solid #d8d8d8 !important;
    }
  }

  .button {
    margin-bottom: 24px;
  }

  .link {
    text-align: center;
    .signUpLink {
      cursor: pointer;

      border-bottom: 1px solid #050505;
    }
  }
}
</style>
